import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import ScorecardNavigation from '../ScorecardNavigation';
import {
  approveCallout,
  CalloutRow,
  CalloutWeek,
  CalloutWeekSummary,
  hasBothLines,
  isHeldRow,
  isMonday,
  listCallouts,
  listCalloutWeeks,
  rejectCallout,
} from '../../../services/crewCalloutsAdminService';

/**
 * feat-289 — the crew call-out review queue. **Bespoke staff (Admin) only.**
 *
 * The human review gate from spec decision 3: every machine-generated praise
 * line is approved, edited or rejected here before any of it reaches an
 * employee's phone. Nothing with `status != 'approved'` is ever served to a
 * phone (AC8), so a row still sitting at `draft` on Monday evening means that
 * server sees the fallback card that week.
 *
 * ── WHY `AdminRoute` AND NOT `StaffRoute` ──────────────────────────────────
 * `spec.md` §5 names `StaffRoute`, and §5 is wrong. `StaffRoute` admits
 * {Admin, Manager, Corporate} (`components/auth/StaffRoute.tsx:14-15` says so
 * in its own comment). **AC24** requires that no manager, corporate or
 * customer-facing surface renders a delivered line — that is decision 5
 * ("management never sees an employee's call-out"), and since the privacy line
 * was removed from the phone card on 2026-09-16 this route guard is the only
 * place that promise is kept structurally.
 *
 * Every backend endpoint is `Depends(require_admin)` (`CONTRACT-ADMIN.md` §0.1),
 * so a Manager gets 403 regardless and the DATA is safe either way. What
 * `StaffRoute` would leak is not data but EXISTENCE: a restaurant manager would
 * get a rendered shell titled "Crew call-outs" that then fills with errors,
 * which tells them a queue exists and that it is about their own staff.
 *
 * The behavioural half of that promise is asserted in
 * `components/auth/__tests__/feat289CrewCalloutsGuard.test.tsx`; the route-table
 * half is in that same file and in `src/__tests__/routeGuardMap.test.ts`.
 *
 * ── WHY THE SOURCE IS ON EVERY ROW ─────────────────────────────────────────
 * A reviewer must be able to CHECK the claim rather than trust it — that is the
 * entire point of the queue, and a line whose source is one click away is a line
 * nobody checks. So `source_title` (verbatim, never paraphrased) sits next to
 * the lines it justifies, with a direct link to the shift transcript. The quote
 * text itself is deliberately NOT copied onto the row (`CONTRACT-ADMIN.md`
 * §0.2): a copied quote can drift from the transcript it claims to come from,
 * and noticing that drift is the reviewer's job. Link, don't duplicate.
 *
 * SECURITY: `line_en`, `line_es`, `source_title`, and feat-322's `basis` and
 * `hold_reasons` are UNTRUSTED (LLM-written or transcript-derived). They are rendered ONLY as `{value}` JSX text nodes and
 * bound to controlled inputs. No `dangerouslySetInnerHTML` in this file, ever.
 */

// ============================================================================
// Copy. Constants, not literals — a literal-text grep must not return a false
// zero for text that is visibly on the page (interviewer-frontend/CLAUDE.md).
// ============================================================================

export const CALLOUT_COPY = {
  pageTitle: 'Crew call-outs',
  pageSubtitle:
    'Weekly praise lines. Nothing reaches a phone until it is approved here.',

  weekLabel: 'Week of',
  locationLabel: 'Location',
  allLocations: 'All locations',

  loading: 'Loading the week…',
  loadFailed: 'Could not load this week.',
  retry: 'Try again',

  draftsPending: 'to review',
  noCandidateLabel: 'servers with no line this week',
  noCandidateHelp:
    'They worked this week and the pipeline produced nothing for them, so they will see the fallback card. This number is invisible everywhere else in the system.',
  approvedLabel: 'approved',
  rejectedLabel: 'rejected',

  // Two different empty weeks, and they mean opposite things. "Reviewed" is a
  // finished week; "nothing proposed" is a week the pipeline had nothing for.
  // Collapsing them into one "No results" is how a broken job reads as a quiet
  // one.
  allReviewedTitle: 'This week is reviewed.',
  allReviewedBody:
    'Every line proposed for this week has been approved or rejected. Nothing is waiting on you.',
  nothingProposedTitle: 'No lines were proposed for this week.',
  nothingProposedBody:
    'The selector found nothing that qualified, or the weekly job did not run. Every server who worked will see the fallback card.',

  statusDraft: 'Needs review',
  statusHeld: 'Needs a line',
  statusApproved: 'Approved',
  statusRejected: 'Rejected',
  editedChip: 'Edited',
  deliveredChip: 'Delivered',

  // feat-322 — which step of the job's fallback chain produced the row. A held
  // row shows none of these: its "Needs a line" status chip is the badge.
  sourceHighlight: 'Highlight',
  sourceHighlightFallback: 'Highlight (fallback)',
  sourceTranscript: 'Transcript',

  // feat-322: the job now tries several sources with three attempts each, so
  // "failed twice" stopped being true (coordinator ruling on DESIGN-QUESTION Q1).
  heldExplainer:
    'Nothing we generated passed the checks, so this row is holding the best source we found. Write both lines, then approve.',
  // Rendered as `${holdReasonsSummary} (${n})`.
  holdReasonsSummary: 'Why it was held',

  lineEnLabel: 'English',
  lineEsLabel: 'Spanish',
  noLineYet: 'No line yet',
  sourceLabel: 'Source',
  // Appended to `sourceLabel` when the source shown is a paraphrased `basis`.
  sourceParaphrased: ' · paraphrased',
  openTranscript: 'Open the shift transcript',
  noSource: 'No source recorded on this row.',
  reviewedBy: 'Reviewed by',
  interactionRef: 'Interaction',

  approve: 'Approve',
  editAndApprove: 'Edit & approve',
  saveAndApprove: 'Save & approve',
  cancel: 'Cancel',
  reject: 'Reject',
  working: 'Working…',
  bothLinesRequired:
    'Both languages are required. One edited half and one left as the machine wrote it is the state nobody intends.',
  deliveredCaution:
    'This line is already on a phone. Changing it now will not take it back.',
  actionFailed: 'That did not save.',

  notMondayWarning:
    'A week must start on a Monday. Showing the week awaiting review instead.',
} as const;

// ============================================================================
// Presentation helpers
// ============================================================================

/**
 * Four visual states, not three. A **held** draft (`line_en === null`,
 * `CONTRACT-ADMIN.md` §2 / D10 / AC31) is a different job from a machine-written
 * draft: one is "check this", the other is "write this". Rendered as an empty
 * line a held row looks like a bug, and the reviewer reports it as one.
 */
export type CalloutVisualState = 'held' | 'draft' | 'approved' | 'rejected';

export function visualState(row: CalloutRow): CalloutVisualState {
  if (isHeldRow(row)) return 'held';
  if (row.status === 'approved') return 'approved';
  if (row.status === 'rejected') return 'rejected';
  return 'draft';
}

/**
 * Static class strings per state — never interpolated. Tailwind purges on a
 * literal scan of the source, so `bg-${colour}-100` produces a class that is not
 * in the built CSS and a badge that silently renders unstyled.
 *
 * A `draft` and a `rejected` row are visually distinguishable from an
 * `approved` one, which is an acceptance criterion and not a preference: the
 * three carry different borders AND different badge colours, so the distinction
 * survives a greyscale screenshot and a reviewer who does not know the palette.
 */
const STATE_STYLES: Record<
  CalloutVisualState,
  { card: string; badge: string; label: string }
> = {
  held: {
    card: 'border-violet-300 bg-violet-50/40',
    badge: 'bg-violet-100 text-violet-800 border border-violet-300',
    label: CALLOUT_COPY.statusHeld,
  },
  draft: {
    card: 'border-amber-300 bg-white',
    badge: 'bg-amber-100 text-amber-800 border border-amber-300',
    label: CALLOUT_COPY.statusDraft,
  },
  approved: {
    card: 'border-emerald-200 bg-emerald-50/30',
    badge: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
    label: CALLOUT_COPY.statusApproved,
  },
  rejected: {
    card: 'border-gray-200 bg-gray-50 opacity-80',
    badge: 'bg-gray-200 text-gray-700 border border-gray-300',
    label: CALLOUT_COPY.statusRejected,
  },
};

/**
 * feat-322 — the source chip's label, or `null` for no chip.
 *
 * - A held row gets none, whatever its `source_kind`: the "Needs a line" status
 *   chip is its badge (design/NOTES.md §1, coordinator ruling Q2).
 * - `null`, absent (pre-feat-322 row) or an unknown string gets none — no
 *   crash, and no neutral placeholder.
 */
export function sourceKindLabel(row: CalloutRow): string | null {
  if (isHeldRow(row)) return null;
  switch (row.source_kind) {
    case 'highlight':
      return CALLOUT_COPY.sourceHighlight;
    case 'highlight_fallback':
      return CALLOUT_COPY.sourceHighlightFallback;
    case 'transcript':
      return CALLOUT_COPY.sourceTranscript;
    default:
      return null;
  }
}

/**
 * feat-322 — what the Source block shows.
 *
 * - `paraphrased`: the row's `basis` (or, defensively, `source_title`) rendered
 *   unquoted and not italic. Used when `source_kind === 'transcript'`, or when
 *   the row has a `basis` but no `source_title` (coordinator ruling Q3).
 * - `verbatim`: today's quoted, italic `source_title` — highlight, fallback,
 *   and legacy rows. Italic + quotes is the shipped signal for "verbatim", so a
 *   paraphrase must never wear it.
 * - `null`: nothing recorded.
 */
export type SourceDisplay =
  | { mode: 'paraphrased'; text: string }
  | { mode: 'verbatim'; text: string }
  | null;

export function sourceDisplay(row: CalloutRow): SourceDisplay {
  const basis = row.basis || null;
  const title = row.source_title || null;
  if (row.source_kind === 'transcript' || !title) {
    const text = basis ?? title;
    return text ? { mode: 'paraphrased', text } : null;
  }
  return { mode: 'verbatim', text: title };
}

/**
 * One class for every source value, neutral on purpose: colour on this card
 * already means review status (violet / amber / emerald / gray), and a second
 * hue axis would make "Transcript" read as a status. Static, never interpolated.
 */
const SOURCE_CHIP_CLASS = 'bg-white text-inkMuted border border-gray-300';

/** `YYYY-MM-DD` → "Mon 14 Sep 2026", read as a UTC calendar date.
 *  The string is a location-local day; re-reading it in the browser's zone
 *  would show the day before for anyone west of UTC — Honolulu, i.e. the pilot. */
export function formatCalendarDate(value: string | null): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const [y, m, d] = value.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** One entry per location present in the week, for the filter. There is no
 *  locations endpoint and none is needed — `location_name` is on every row
 *  (`CONTRACT-ADMIN.md` §5). */
export function locationOptionsFrom(
  rows: CalloutRow[]
): Array<{ id: string; name: string }> {
  const seen = new Map<string, string>();
  for (const r of rows) {
    if (!r.location_id) continue;
    if (!seen.has(r.location_id)) {
      seen.set(r.location_id, r.location_name ?? r.location_id);
    }
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ============================================================================
// Row
// ============================================================================

const Chip: React.FC<{ className: string; children: React.ReactNode; testId?: string }> = ({
  className,
  children,
  testId,
}) => (
  <span
    className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${className}`}
    data-testid={testId}
  >
    {children}
  </span>
);

/** One language half. Untrusted text, rendered as a JSX text node only. */
const LineHalf: React.FC<{ label: string; value: string | null; testId: string }> = ({
  label,
  value,
  testId,
}) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted mb-1">
      {label}
    </div>
    <div
      className="text-sm text-ink/90 whitespace-pre-wrap bg-white border border-gray-200 rounded p-2 min-h-[2.5rem]"
      data-testid={testId}
    >
      {value != null && value !== '' ? (
        value
      ) : (
        <span className="italic text-inkMuted">{CALLOUT_COPY.noLineYet}</span>
      )}
    </div>
  </div>
);

/** One language half, editable. Controlled `value` — never `innerHTML`. */
const EditableHalf: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  testId: string;
}> = ({ label, value, onChange, disabled, testId }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-inkMuted mb-1">
      {label}
      <textarea
        className="mt-1 w-full text-sm text-ink bg-white border border-gray-300 rounded p-2 min-h-[4.5rem] font-normal normal-case tracking-normal"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </label>
  </div>
);

export interface CalloutCardProps {
  row: CalloutRow;
  busy: boolean;
  /** Undefined `edit` → approve the machine's lines unchanged, `edited` stays false. */
  onApprove: (row: CalloutRow, edit?: { line_en: string; line_es: string }) => void;
  onReject: (row: CalloutRow) => void;
  /** Message from the last failed action on THIS row, if any. */
  error?: string | null;
}

export const CalloutCard: React.FC<CalloutCardProps> = ({
  row,
  busy,
  onApprove,
  onReject,
  error,
}) => {
  const state = visualState(row);
  const styles = STATE_STYLES[state];

  // A held row has nothing to approve, so its editor is open from the start and
  // there is no plain Approve button on it — see the actions block below.
  const held = state === 'held';
  const [editing, setEditing] = useState(held);
  const [draftEn, setDraftEn] = useState(row.line_en ?? '');
  const [draftEs, setDraftEs] = useState(row.line_es ?? '');

  /**
   * Re-sync the editor when the ROW ITSELF changes — a week loads, or an action
   * returns the updated row. Adjusted DURING RENDER (React's documented
   * "adjusting state when a prop changes" pattern), deliberately, and NOT in an
   * effect.
   *
   * An effect here is wrong in a way that is easy to miss and was caught by a
   * test: passive effects run AFTER the DOM is committed, so between the paint
   * and the effect there is a window in which the buttons are on screen and the
   * mount effect has not run yet. A click landing in that window sets
   * `editing = true`, the mount effect then runs and sets it straight back to
   * `false`, and the editor never opens. Under a loaded parallel jest run that
   * window is wide enough to hit every time; in a browser it is a few
   * milliseconds of a real person's click.
   *
   * Adjusting during render also means this never runs on mount at all —
   * `useState` already initialised both drafts and `editing` from the same row.
   */
  const [syncedRow, setSyncedRow] = useState(row);
  if (row !== syncedRow) {
    setSyncedRow(row);
    setDraftEn(row.line_en ?? '');
    setDraftEs(row.line_es ?? '');
    setEditing(isHeldRow(row));
  }

  const bothPresent = hasBothLines(draftEn, draftEs);
  const sourceChip = sourceKindLabel(row);
  const source = sourceDisplay(row);
  const holdReasons = Array.isArray(row.hold_reasons) ? row.hold_reasons : [];

  return (
    <li
      className={`border rounded-lg p-4 ${styles.card}`}
      data-testid={`callout-row-${row.id}`}
      data-state={state}
    >
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-base font-display font-semibold text-ink">
          {row.first_name}
        </span>
        {row.location_name && (
          <span className="text-xs text-inkMuted">{row.location_name}</span>
        )}
        {row.event_date && (
          <span className="text-xs text-inkMuted">
            {formatCalendarDate(row.event_date)}
          </span>
        )}
        <Chip className={styles.badge} testId={`callout-status-${row.id}`}>
          {styles.label}
        </Chip>
        {sourceChip && (
          <Chip className={SOURCE_CHIP_CLASS} testId={`callout-source-kind-${row.id}`}>
            {sourceChip}
          </Chip>
        )}
        {row.edited && (
          <Chip className="bg-sky-100 text-sky-800 border border-sky-300">
            {CALLOUT_COPY.editedChip}
          </Chip>
        )}
        {row.delivered_at && (
          <Chip className="bg-indigo-100 text-indigo-800 border border-indigo-300">
            {CALLOUT_COPY.deliveredChip}
          </Chip>
        )}
      </div>

      {state === 'held' && (
        // A <div>, not a <p>: the <details> below cannot live inside a <p>.
        <div
          className="text-sm text-violet-900 bg-violet-100 border border-violet-200 rounded p-2 mb-3"
          data-testid={`callout-held-${row.id}`}
        >
          <p>{CALLOUT_COPY.heldExplainer}</p>
          {/* feat-322: why the job gave up, per step, in the order recorded.
              Closed by default — it is secondary to writing the line. Untrusted
              text (it echoes model output), rendered as text nodes only. */}
          {holdReasons.length > 0 && (
            <details className="mt-1" data-testid={`callout-hold-reasons-${row.id}`}>
              <summary className="cursor-pointer text-xs font-semibold text-violet-900">
                {`${CALLOUT_COPY.holdReasonsSummary} (${holdReasons.length})`}
              </summary>
              <ul className="mt-1 space-y-0.5 text-xs font-mono text-violet-900 break-words list-none">
                {holdReasons.map((reason, i) => (
                  // Reasons can repeat verbatim across attempts; index keeps keys unique.
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Both languages together, never behind a toggle: a reviewer approves
          both at once or the Spanish ships unread. Editing is likewise both
          halves at once — the backend answers a one-sided edit with 400, and the
          reason is the same one. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {editing ? (
          <>
            <EditableHalf
              label={CALLOUT_COPY.lineEnLabel}
              value={draftEn}
              onChange={setDraftEn}
              disabled={busy}
              testId={`callout-edit-en-${row.id}`}
            />
            <EditableHalf
              label={CALLOUT_COPY.lineEsLabel}
              value={draftEs}
              onChange={setDraftEs}
              disabled={busy}
              testId={`callout-edit-es-${row.id}`}
            />
          </>
        ) : (
          <>
            <LineHalf
              label={CALLOUT_COPY.lineEnLabel}
              value={row.line_en}
              testId={`callout-line-en-${row.id}`}
            />
            <LineHalf
              label={CALLOUT_COPY.lineEsLabel}
              value={row.line_es}
              testId={`callout-line-es-${row.id}`}
            />
          </>
        )}
      </div>

      {editing && !bothPresent && (
        <p
          className="mt-2 text-xs text-amber-900"
          data-testid={`callout-both-required-${row.id}`}
        >
          {CALLOUT_COPY.bothLinesRequired}
        </p>
      )}

      {/* The source, one click from the transcript it came from. Verbatim
          highlight titles are quoted and italic; a paraphrased `basis`
          (feat-322) is neither, and its label says so. */}
      <div className="mt-3 border-t border-gray-200 pt-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted mb-1">
          {CALLOUT_COPY.sourceLabel}
          {source?.mode === 'paraphrased' && (
            <span className="font-normal normal-case tracking-normal">
              {CALLOUT_COPY.sourceParaphrased}
            </span>
          )}
        </div>
        {source?.mode === 'paraphrased' ? (
          <p className="text-sm text-ink/80" data-testid={`callout-basis-${row.id}`}>
            {source.text}
          </p>
        ) : source?.mode === 'verbatim' ? (
          <p
            className="text-sm text-ink/80 italic"
            data-testid={`callout-source-${row.id}`}
          >
            “{source.text}”
          </p>
        ) : (
          <p className="text-sm text-inkMuted italic">{CALLOUT_COPY.noSource}</p>
        )}
        <div className="flex items-center gap-3 flex-wrap mt-1">
          {row.source_shift_id && (
            <Link
              to={`/restaurant/shifts/${encodeURIComponent(
                row.source_shift_id
              )}/transcript/${encodeURIComponent(row.server_id)}`}
              className="text-sm text-coral hover:underline"
              data-testid={`callout-transcript-link-${row.id}`}
            >
              {CALLOUT_COPY.openTranscript}
            </Link>
          )}
          {/* The interaction reference, rendered because the link above lands at
              the TOP of the shift's transcript: the row carries no time offset,
              so there is nothing to put in `?t=`. Raised as Q1 in
              QUESTIONS-FRONTEND.md — a `source_offset_seconds` on the row would
              let the link land on the moment. Until then this at least gives the
              reviewer something to search for. */}
          {row.source_interaction_id && (
            <span
              className="text-xs text-inkMuted font-mono"
              data-testid={`callout-interaction-ref-${row.id}`}
            >
              {CALLOUT_COPY.interactionRef} {row.source_interaction_id}
            </span>
          )}
        </div>
      </div>

      {row.reviewed_by && (
        <p className="mt-3 text-xs text-inkMuted" data-testid={`callout-reviewed-${row.id}`}>
          {CALLOUT_COPY.reviewedBy} {row.reviewed_by}
          {row.reviewed_at ? ` · ${new Date(row.reviewed_at).toLocaleString()}` : ''}
        </p>
      )}

      {row.delivered_at && (
        <p
          className="mt-3 text-xs text-indigo-900 bg-indigo-50 border border-indigo-200 rounded p-2"
          data-testid={`callout-delivered-caution-${row.id}`}
        >
          {CALLOUT_COPY.deliveredCaution}
        </p>
      )}

      {/* Actions.
          They are rendered on EVERY row, whatever its status, and that is a
          decision: approval is what puts a line on a phone (AC8) and one
          misclick would otherwise be permanent. Nothing in CONTRACT-ADMIN.md §1
          documents a status precondition on either endpoint or a 409 in §4, so
          reject undoes an approve and approve undoes a reject. Raised as Q3.

          A HELD row gets no plain Approve. Approving it with no body would
          approve `line_en: null` — a row that satisfies AC8 and delivers
          nothing, which is precisely the silent fallback D10 exists to prevent.
          Its only path forward is writing both lines. */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {editing ? (
          <>
            <button
              type="button"
              className="px-3 py-1.5 text-sm font-semibold rounded bg-coral text-white disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={busy || !bothPresent}
              onClick={() => onApprove(row, { line_en: draftEn.trim(), line_es: draftEs.trim() })}
              data-testid={`callout-save-approve-${row.id}`}
            >
              {busy ? CALLOUT_COPY.working : CALLOUT_COPY.saveAndApprove}
            </button>
            {!held && (
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded border border-softBorder text-ink bg-white disabled:opacity-40"
                disabled={busy}
                onClick={() => {
                  setDraftEn(row.line_en ?? '');
                  setDraftEs(row.line_es ?? '');
                  setEditing(false);
                }}
                data-testid={`callout-cancel-${row.id}`}
              >
                {CALLOUT_COPY.cancel}
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="px-3 py-1.5 text-sm font-semibold rounded bg-coral text-white disabled:opacity-40"
              disabled={busy}
              onClick={() => onApprove(row)}
              data-testid={`callout-approve-${row.id}`}
            >
              {busy ? CALLOUT_COPY.working : CALLOUT_COPY.approve}
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded border border-softBorder text-ink bg-white disabled:opacity-40"
              disabled={busy}
              onClick={() => setEditing(true)}
              data-testid={`callout-edit-${row.id}`}
            >
              {CALLOUT_COPY.editAndApprove}
            </button>
          </>
        )}
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded border border-rose-300 text-rose-800 bg-white disabled:opacity-40"
          disabled={busy}
          onClick={() => onReject(row)}
          data-testid={`callout-reject-${row.id}`}
        >
          {CALLOUT_COPY.reject}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-sm text-rose-800" data-testid={`callout-error-${row.id}`}>
          {CALLOUT_COPY.actionFailed} {error}
        </p>
      )}
    </li>
  );
};

// ============================================================================
// Page
// ============================================================================

const CrewCalloutReviewQueue: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // `week_start` from the URL so a reviewer can link a colleague at a week.
  // A non-Monday is a 400 by design (an empty queue is indistinguishable from a
  // week nobody qualified for), so a bad one is dropped here with a visible
  // warning rather than sent and rendered as a server error.
  const rawWeek = searchParams.get('week') ?? '';
  const weekParamIsBad = rawWeek !== '' && !isMonday(rawWeek);
  const weekStart = weekParamIsBad ? '' : rawWeek;
  const locationId = searchParams.get('location') ?? '';

  const [week, setWeek] = useState<CalloutWeek | null>(null);
  const [weeks, setWeeks] = useState<CalloutWeekSummary[]>([]);
  const [locationOptions, setLocationOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCallouts({
        weekStart: weekStart || undefined,
        locationId: locationId || undefined,
      });
      setWeek(data);
      // Options come only from an UNFILTERED response. Deriving them from the
      // filtered rows would leave the select holding exactly the location
      // already chosen, with no way back to the others.
      if (!locationId) setLocationOptions(locationOptionsFrom(data.rows));
    } catch (e) {
      setError(e instanceof Error ? e.message : CALLOUT_COPY.loadFailed);
      setWeek(null);
    } finally {
      setLoading(false);
    }
  }, [weekStart, locationId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    listCalloutWeeks()
      .then((w) => {
        if (!cancelled) setWeeks(w);
      })
      // The picker is a convenience; losing it must not take the queue with it.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Changing the week clears the location: a location that exists in one
    // week may have no rows in another, and a filter silently emptying the
    // queue is the exact failure the whole surface is built to prevent.
    if (key === 'week') next.delete('location');
    setSearchParams(next, { replace: true });
  };

  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  /**
   * Apply one action and replace the row with what the backend returned.
   *
   * The returned `CalloutRow` is the source of truth for `status`,
   * `reviewed_by`, `reviewed_at` and `edited` (AC23) — none of those are guessed
   * client-side. `edited` in particular: the backend sets it, and a client that
   * inferred it from "I sent a body" would disagree with the row the moment the
   * rules changed.
   */
  const runAction = useCallback(
    async (row: CalloutRow, action: () => Promise<CalloutRow>) => {
      setBusyId(row.id);
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      try {
        const updated = await action();
        setWeek((prev) =>
          prev
            ? { ...prev, rows: prev.rows.map((r) => (r.id === updated.id ? updated : r)) }
            : prev
        );
      } catch (e) {
        // Left on the row, not in a page-level banner: the reviewer needs to
        // know WHICH line failed, and a toast that has scrolled away is how a
        // 400 on one row reads as "the page is broken".
        setRowErrors((prev) => ({
          ...prev,
          [row.id]: e instanceof Error ? e.message : String(e),
        }));
      } finally {
        setBusyId(null);
      }
    },
    []
  );

  const handleApprove = useCallback(
    (row: CalloutRow, edit?: { line_en: string; line_es: string }) =>
      runAction(row, () => approveCallout(row.id, edit)),
    [runAction]
  );

  const handleReject = useCallback(
    (row: CalloutRow) => runAction(row, () => rejectCallout(row.id)),
    [runAction]
  );

  const rows = useMemo(() => week?.rows ?? [], [week]);

  /**
   * The three status counts are DERIVED from `rows`, not read from the
   * envelope's `draft_count`. They have to stay live as the reviewer works —
   * an envelope figure would freeze at load and still read "3 to review" after
   * the reviewer had cleared all three, which is a worse lie than no number.
   * The envelope's count is the same figure at load time.
   *
   * `no_candidate_count` is the opposite case and comes straight from the
   * envelope: it counts servers with NO ROW AT ALL and is not derivable from
   * `rows` by construction (CONTRACT-ADMIN.md §2).
   */
  const draftCount = useMemo(
    () => rows.filter((r) => r.status === 'draft').length,
    [rows]
  );
  const approvedCount = useMemo(
    () => rows.filter((r) => r.status === 'approved').length,
    [rows]
  );
  const rejectedCount = useMemo(
    () => rows.filter((r) => r.status === 'rejected').length,
    [rows]
  );

  /** Two empty weeks with opposite meanings — see CALLOUT_COPY. */
  const emptyKind: 'none' | 'all-reviewed' | 'nothing-proposed' =
    rows.length === 0 ? 'nothing-proposed' : draftCount === 0 ? 'all-reviewed' : 'none';

  // The week the picker should show as selected: the URL's, or — when the URL
  // says nothing — whatever the backend chose as the week awaiting review.
  const selectedWeek = weekStart || week?.week_start || '';

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="crew-callouts-page">
        <h1 className="text-2xl font-display font-semibold text-ink">
          {CALLOUT_COPY.pageTitle}
        </h1>
        <p className="text-sm text-inkMuted mt-1">{CALLOUT_COPY.pageSubtitle}</p>

        {weekParamIsBad && (
          <p
            className="mt-3 text-sm text-amber-900 bg-amber-100 border border-amber-300 rounded p-2"
            data-testid="crew-callouts-bad-week"
          >
            {CALLOUT_COPY.notMondayWarning}
          </p>
        )}

        {/* Controls */}
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="text-sm text-inkMuted">
            <span className="block text-[11px] font-semibold uppercase tracking-wide mb-1">
              {CALLOUT_COPY.weekLabel}
            </span>
            <select
              className="border border-softBorder rounded px-2 py-1 text-sm text-ink bg-white"
              value={selectedWeek}
              onChange={(e) => setParam('week', e.target.value)}
              data-testid="crew-callouts-week-select"
            >
              {/* The backend's default week may not be in the first page of the
                  picker; keep it selectable rather than snapping the control to
                  a week the page is not showing. */}
              {selectedWeek && !weeks.some((w) => w.week_start === selectedWeek) && (
                <option value={selectedWeek}>{formatCalendarDate(selectedWeek)}</option>
              )}
              {weeks.map((w) => (
                <option key={w.week_start} value={w.week_start}>
                  {formatCalendarDate(w.week_start)}
                  {w.drafts > 0 ? ` — ${w.drafts} ${CALLOUT_COPY.draftsPending}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-inkMuted">
            <span className="block text-[11px] font-semibold uppercase tracking-wide mb-1">
              {CALLOUT_COPY.locationLabel}
            </span>
            <select
              className="border border-softBorder rounded px-2 py-1 text-sm text-ink bg-white"
              value={locationId}
              onChange={(e) => setParam('location', e.target.value)}
              data-testid="crew-callouts-location-select"
            >
              <option value="">{CALLOUT_COPY.allLocations}</option>
              {locationOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* The counts band. `no_candidate_count` is rendered HERE, at the top,
            beside the week it belongs to — not in a tooltip. It is the number
            that tells Bespoke whether the pipeline is working, it is invisible
            everywhere else in the system, and a queue of six good lines looks
            identical whether the other forty servers had nothing that qualified
            or the job stopped after six. */}
        {week && (
          <div
            className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3"
            data-testid="crew-callouts-counts"
          >
            <div className="bg-white border border-softBorder rounded-lg p-3">
              <div className="text-2xl font-display font-semibold text-ink">
                {draftCount}
              </div>
              <div className="text-xs text-inkMuted">{CALLOUT_COPY.draftsPending}</div>
            </div>
            <div className="bg-white border border-softBorder rounded-lg p-3">
              <div className="text-2xl font-display font-semibold text-ink">
                {approvedCount}
              </div>
              <div className="text-xs text-inkMuted">{CALLOUT_COPY.approvedLabel}</div>
            </div>
            <div className="bg-white border border-softBorder rounded-lg p-3">
              <div className="text-2xl font-display font-semibold text-ink">
                {rejectedCount}
              </div>
              <div className="text-xs text-inkMuted">{CALLOUT_COPY.rejectedLabel}</div>
            </div>
            <div className="bg-white border-2 border-coral rounded-lg p-3 sm:col-span-1">
              <div
                className="text-2xl font-display font-semibold text-ink"
                data-testid="crew-callouts-no-candidate-count"
              >
                {week.no_candidate_count}
              </div>
              <div className="text-xs text-inkMuted">
                {CALLOUT_COPY.noCandidateLabel}
              </div>
            </div>
          </div>
        )}
        {week && (
          <p className="mt-2 text-xs text-inkMuted max-w-2xl">
            {CALLOUT_COPY.noCandidateHelp}
          </p>
        )}

        {/* Body */}
        {loading && (
          <p className="mt-6 text-sm text-inkMuted" data-testid="crew-callouts-loading">
            {CALLOUT_COPY.loading}
          </p>
        )}

        {!loading && error && (
          <div
            className="mt-6 bg-white border border-rose-300 rounded-lg p-4"
            data-testid="crew-callouts-error"
          >
            <p className="text-sm text-rose-800">{CALLOUT_COPY.loadFailed}</p>
            <p className="text-xs text-inkMuted mt-1">{error}</p>
            <button
              type="button"
              className="mt-2 text-sm text-coral hover:underline"
              onClick={() => load()}
            >
              {CALLOUT_COPY.retry}
            </button>
          </div>
        )}

        {!loading && !error && emptyKind !== 'none' && (
          <div
            className="mt-6 bg-white border border-softBorder rounded-lg p-6 text-center"
            data-testid={`crew-callouts-empty-${emptyKind}`}
          >
            <p className="text-base font-display font-semibold text-ink">
              {emptyKind === 'all-reviewed'
                ? CALLOUT_COPY.allReviewedTitle
                : CALLOUT_COPY.nothingProposedTitle}
            </p>
            <p className="text-sm text-inkMuted mt-1">
              {emptyKind === 'all-reviewed'
                ? CALLOUT_COPY.allReviewedBody
                : CALLOUT_COPY.nothingProposedBody}
            </p>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <ul className="mt-6 space-y-4" data-testid="crew-callouts-list">
            {rows.map((row) => (
              <CalloutCard
                key={row.id}
                row={row}
                busy={busyId === row.id}
                onApprove={handleApprove}
                onReject={handleReject}
                error={rowErrors[row.id] ?? null}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CrewCalloutReviewQueue;
